/** Completed evaluation scorecard grouped by config/model, then prompt. */

import { useState } from 'react';
import { cn } from '@/shared/lib/cn';
import type { EvalCombo, EvalResultCell, EvalRun } from '@/entities/eval';

function promptLabel(combo: EvalCombo): string {
  return combo.prompt_role === 'baseline' || combo.role === 'baseline'
    ? `Active prompt${combo.prompt_version_number != null ? ` v${combo.prompt_version_number}` : ''}`
    : `Draft prompt${combo.prompt_version_number != null
      ? ` v${combo.prompt_version_number}`
      : ` ${combo.prompt_version_id?.slice(0, 6) ?? ''}`}`.trim();
}

function configLabel(combo: EvalCombo): string {
  const version = combo.config_role === 'candidate'
    ? `Config${combo.config_version_number != null
      ? ` v${combo.config_version_number}`
      : ` ${combo.config_version_id?.slice(0, 6) ?? 'draft'}`}`
    : `Active config${combo.config_version_number != null ? ` v${combo.config_version_number}` : ''}`;
  const model = [combo.provider, combo.model].filter(Boolean).join(' / ');
  return model ? `${version} · ${model}` : version;
}

function comboLabel(combo: EvalCombo): string {
  return `${configLabel(combo)} · ${promptLabel(combo)}`;
}

function scoreClass(score: number | null | undefined, passThreshold?: number | null): string {
  if (score === null || score === undefined) return 'text-muted-foreground';
  if (passThreshold !== null && passThreshold !== undefined) {
    return score >= passThreshold
      ? 'text-status-success font-semibold'
      : 'text-destructive font-semibold';
  }
  if (score >= 80) return 'text-status-success font-semibold';
  if (score >= 60) return 'text-foreground';
  return 'text-destructive font-semibold';
}

function scoreBarClass(score: number | null): string {
  if (score === 100) return 'w-full';
  if (score === 75) return 'w-3/4';
  if (score === 50) return 'w-1/2';
  if (score === 25) return 'w-1/4';
  return 'w-0';
}

const cellKey = (runName: string, testCaseId: string) => `${runName}::${testCaseId}`;

export default function ScorecardWidget({
  run,
  passThreshold,
}: {
  run: EvalRun;
  passThreshold?: number | null;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const combos = run.combos ?? [];
  const results = run.results ?? [];
  if (combos.length === 0 || results.length === 0) return null;

  const rowOrder: string[] = [];
  const rowName = new Map<string, string>();
  const rowSplit = new Map<string, EvalResultCell['split']>();
  for (const cell of results) {
    if (!rowName.has(cell.test_case_id)) {
      rowName.set(cell.test_case_id, cell.test_case_name);
      rowSplit.set(cell.test_case_id, cell.split);
      rowOrder.push(cell.test_case_id);
    }
  }

  const groups: Array<{ key: string; label: string; combos: EvalCombo[] }> = [];
  for (const combo of combos) {
    const key = combo.config_version_id ?? `active:${combo.provider ?? ''}:${combo.model ?? ''}`;
    const existing = groups.find((group) => group.key === key);
    if (existing) existing.combos.push(combo);
    else groups.push({ key, label: configLabel(combo), combos: [combo] });
  }

  const cellFor = (runName: string, testCaseId: string): EvalResultCell | undefined =>
    results.find((cell) => cell.run_name === runName && cell.test_case_id === testCaseId);
  const activeCombo = combos.find((combo) => combo.role === 'baseline');
  const isRegression = (combo: EvalCombo, cell: EvalResultCell | undefined): boolean => {
    if (combo.role !== 'candidate' || cell?.status !== 'scored' || typeof cell.overall !== 'number') return false;
    const baseline = activeCombo ? cellFor(activeCombo.run_name, cell.test_case_id) : undefined;
    return baseline?.status === 'scored' && typeof baseline.overall === 'number' && cell.overall < baseline.overall;
  };
  const winners = new Set(run.comparison?.winner_run_names ?? []);
  const expandedCell = expanded
    ? results.find((cell) => cellKey(cell.run_name, cell.test_case_id) === expanded)
    : undefined;
  const expandedCombo = expandedCell ? combos.find((combo) => combo.run_name === expandedCell.run_name) : undefined;

  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <th rowSpan={2} className="border-b border-border p-2 text-left font-medium text-muted-foreground">
                Test case
              </th>
              {groups.map((group) => (
                <th
                  key={group.key}
                  colSpan={group.combos.length}
                  className="border-b border-border p-2 text-center font-semibold text-foreground"
                >
                  {group.label}
                  <span className="block text-xs font-normal text-muted-foreground">
                    effort {group.combos[0]?.effort ?? '—'} · max iterations {group.combos[0]?.max_iterations ?? '—'}
                  </span>
                </th>
              ))}
            </tr>
            <tr>
              {combos.map((combo) => (
                <th
                  key={combo.run_name}
                  className={cn(
                    'border-b border-border p-2 text-center font-medium',
                    winners.has(combo.run_name) ? 'text-[var(--color-status-success)]' : 'text-foreground',
                  )}
                >
                  {promptLabel(combo)}{winners.has(combo.run_name) && ' 🏆'}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowOrder.map((testCaseId) => (
              <tr key={testCaseId}>
                <td className="border-b border-border p-2 text-foreground">
                  <span>{rowName.get(testCaseId)}</span>
                  <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                    {rowSplit.get(testCaseId)}
                  </span>
                </td>
                {combos.map((combo) => {
                  const cell = cellFor(combo.run_name, testCaseId);
                  const key = cellKey(combo.run_name, testCaseId);
                  return (
                    <td key={combo.run_name} className="border-b border-border p-1 text-center">
                      <button
                        type="button"
                        onClick={() => setExpanded(expanded === key ? null : key)}
                        className={cn(
                          'w-full rounded px-2 py-1 hover:bg-muted',
                          scoreClass(cell?.overall, passThreshold),
                          expanded === key && 'ring-2 ring-ring',
                        )}
                        aria-label={`${rowName.get(testCaseId)} — ${comboLabel(combo)}: ${
                          cell?.status === 'error' ? 'errored' : (cell?.overall ?? 'no result')
                        }`}
                      >
                        {cell?.status === 'error' ? 'err' : (cell?.overall ?? '—')}
                        {isRegression(combo, cell) && <span title="Regression vs active control"> ⚠</span>}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="p-2 text-right font-medium text-muted-foreground">Quality average</td>
              {combos.map((combo) => {
                const coverage = run.coverage[combo.run_name];
                const average = coverage?.average ?? null;
                return (
                  <td
                    key={combo.run_name}
                    className={cn('p-2 text-center font-semibold', scoreClass(average, passThreshold))}
                    aria-label={`Average score for ${comboLabel(combo)}`}
                  >
                    <span>{average ?? '—'}</span>
                    <span className="block text-xs font-normal text-muted-foreground">
                      {coverage?.scored ?? 0}/{coverage?.total ?? rowOrder.length} scored
                    </span>
                  </td>
                );
              })}
            </tr>
            <tr>
              <td className="p-2 text-right text-muted-foreground">Average latency</td>
              {combos.map((combo) => (
                <td key={combo.run_name} className="p-2 text-center text-muted-foreground">
                  {run.coverage[combo.run_name]?.average_duration_ms != null
                    ? `${Math.round(run.coverage[combo.run_name]?.average_duration_ms ?? 0)} ms`
                    : '—'}
                </td>
              ))}
            </tr>
            <tr>
              <td className="p-2 text-right text-muted-foreground">Input / output tokens</td>
              {combos.map((combo) => (
                <td key={combo.run_name} className="p-2 text-center text-muted-foreground">
                  {run.coverage[combo.run_name]?.input_tokens ?? 0} / {run.coverage[combo.run_name]?.output_tokens ?? 0}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>

      {run.comparison && (
        <div className="flex flex-col gap-1 text-sm text-muted-foreground">
          {run.comparison.candidates.map((candidate) => (
            <p key={candidate.candidate_run_name}>
              {promptLabel(combos.find((combo) => combo.run_name === candidate.candidate_run_name) ?? combos[0]!)}:{' '}
              {candidate.paired_scored}/{candidate.holdout_total} holdouts paired
              {candidate.delta !== null ? ` · ${candidate.delta >= 0 ? '+' : ''}${candidate.delta} vs control` : ''}
              {candidate.regression_case_ids.length > 0
                ? ` · ${candidate.regression_case_ids.length} regression${candidate.regression_case_ids.length === 1 ? '' : 's'}`
                : ' · no regressions'}
            </p>
          ))}
          <p className="font-medium text-foreground">
            {winners.size > 0
              ? `Winner${winners.size === 1 ? '' : 's'}: ${[...winners].map((name) => {
                const combo = combos.find((item) => item.run_name === name);
                return combo ? comboLabel(combo) : name;
              }).join(', ')}`
              : 'No qualifying winner.'}
          </p>
        </div>
      )}

      {expandedCell && expandedCombo && (
        <div className="flex flex-col gap-2 rounded-lg border border-border bg-muted/40 p-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="font-medium text-foreground">
              {expandedCell.test_case_name} — {comboLabel(expandedCombo)}
            </span>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => setExpanded(null)}
              aria-label="Close detail"
            >
              ✕
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            {Math.round(expandedCell.duration_ms ?? 0)} ms · {expandedCell.input_tokens ?? 0} input /{' '}
            {expandedCell.output_tokens ?? 0} output tokens
          </p>
          {expandedCell.status === 'scored' ? (
            <>
              <div className="flex flex-col gap-1">
                {Object.entries(expandedCell.dimensions ?? {}).map(([name, score]) => (
                  <div key={name} className="flex items-center gap-2">
                    <span className="w-28 shrink-0 text-muted-foreground">{name}</span>
                    <div className="h-2 flex-1 rounded bg-border">
                      <div className={cn('h-2 rounded bg-[var(--color-status-success)]', scoreBarClass(score))} />
                    </div>
                    <span className="w-8 text-right tabular-nums">{score ?? '—'}</span>
                  </div>
                ))}
              </div>
              {expandedCell.rationale && <p className="text-muted-foreground italic">{expandedCell.rationale}</p>}
            </>
          ) : (
            <p className="text-destructive">{expandedCell.rationale || 'This case errored.'}</p>
          )}
          {expandedCell.output != null && (
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-muted-foreground">Agent answer</span>
              <pre className="max-h-60 overflow-auto rounded border border-border bg-card p-2 text-xs whitespace-pre-wrap text-foreground">
                {expandedCell.output}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
